import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSettings, saveSettings, exportAllData, importAllData, clearAllData, getRecurringSchedules, getClients, getServiceAgreements, saveRecurringSchedule } from '../utils/storageUtils';
import ThemeSelector from '../components/ThemeSelector';
import { useAuth } from '../contexts/AuthContext';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useSubscription } from '../contexts/SubscriptionContext';
import { User, Landmark, FileText, Palette, Lock, Database, Star, Send, MapPin, Zap } from 'lucide-react';
import './Settings.css';

// ─── Tab Definitions ────────────────────────────────────────────
const ICON_SIZE = 18;
function getTabs() {
  return [
    { id: 'profile',       label: 'Profile',         icon: <User size={ICON_SIZE} />, description: 'Business name, address, and contact details' },
    { id: 'bank',          label: 'Bank & Payments',  icon: <Landmark size={ICON_SIZE} />, description: 'Bank account details for receiving payments' },
    { id: 'invoices',      label: 'Invoices',         icon: <FileText size={ICON_SIZE} />, description: 'Invoice numbering and default settings' },
    { id: 'appearance',    label: 'Appearance',        icon: <Palette size={ICON_SIZE} />, description: 'Theme, dark mode, and display preferences' },
    { id: 'security',      label: 'Security',         icon: <Lock size={ICON_SIZE} />, description: 'Password and account security' },
    { id: 'data',          label: 'Data',             icon: <Database size={ICON_SIZE} />, description: 'Export, import, and manage your data' },
    { id: 'subscription',  label: 'Subscription',     icon: <Star size={ICON_SIZE} />, description: 'Plan and billing details' },
    { id: 'autokm', label: 'Auto-Calculate Shift KMs',    icon: <MapPin size={ICON_SIZE} />, description: 'Automatically calculate travel distance for shifts', disabled: true, comingSoon: true },
  ];
}

// ─── Input Formatting Helpers ───────────────────────────────────
const formatBSB = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 6);
  if (digits.length > 3) return digits.slice(0, 3) + '-' + digits.slice(3);
  return digits;
};

const formatABN = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  const parts = [];
  if (digits.length > 0) parts.push(digits.slice(0, 2));
  if (digits.length > 2) parts.push(digits.slice(2, 5));
  if (digits.length > 5) parts.push(digits.slice(5, 8));
  if (digits.length > 8) parts.push(digits.slice(8, 11));
  return parts.join(' ');
};

const formatPostcode = (value) => value.replace(/\D/g, '').slice(0, 4);

// ─── Profile Tab ────────────────────────────────────────────────
function ProfileTab({ settings, onSave }) {
  const [local, setLocal] = useState({
    businessName: settings.businessName || '',
    abn: settings.abn || '',
    address: settings.address || '',
    suburb: settings.suburb || '',
    state: settings.state || '',
    postcode: settings.postcode || '',
    phone: settings.phone || '',
    email: settings.email || '',
    website: settings.website || '',
  });
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formatted = value;
    if (name === 'abn') formatted = formatABN(value);
    if (name === 'postcode') formatted = formatPostcode(value);
    setLocal(prev => ({ ...prev, [name]: formatted }));
    setDirty(true);
    setSaved(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(local);
    setDirty(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <form onSubmit={handleSubmit} aria-label="Business profile">
      <div className="form-group">
        <label htmlFor="settings-businessName">Business Name</label>
        <input id="settings-businessName" type="text" name="businessName" value={local.businessName} onChange={handleChange} />
      </div>
      <div className="form-group">
        <label htmlFor="settings-abn">ABN</label>
        <input id="settings-abn" type="text" name="abn" value={local.abn} onChange={handleChange} placeholder="XX XXX XXX XXX" />
      </div>
      <div className="form-group">
        <label htmlFor="settings-address">Address</label>
        <input id="settings-address" type="text" name="address" value={local.address} onChange={handleChange} />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="settings-suburb">Suburb</label>
          <input id="settings-suburb" type="text" name="suburb" value={local.suburb} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="settings-state">State</label>
          <input id="settings-state" type="text" name="state" value={local.state} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="settings-postcode">Postcode</label>
          <input id="settings-postcode" type="text" name="postcode" value={local.postcode} onChange={handleChange} placeholder="0000" />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="settings-phone">Phone</label>
          <input id="settings-phone" type="tel" name="phone" value={local.phone} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="settings-email">Email</label>
          <input id="settings-email" type="email" name="email" value={local.email} onChange={handleChange} />
        </div>
      </div>
      <div className="form-group">
        <label htmlFor="settings-website">Website</label>
        <input id="settings-website" type="text" name="website" value={local.website} onChange={handleChange} />
      </div>
      <div className="settings-form-footer">
        {dirty && <span className="settings-unsaved">Unsaved changes</span>}
        <button type="submit" className="btn btn-primary" disabled={!dirty}>Save Changes</button>
        {saved && <span className="settings-saved">✓ Saved!</span>}
      </div>
    </form>
  );
}

// ─── Bank Tab ───────────────────────────────────────────────────
function BankTab({ settings, onSave }) {
  const [local, setLocal] = useState({
    bankName: settings.bankName || '',
    bsb: settings.bsb || '',
    accountNumber: settings.accountNumber || '',
  });
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formatted = value;
    if (name === 'bsb') formatted = formatBSB(value);
    setLocal(prev => ({ ...prev, [name]: formatted }));
    setDirty(true);
    setSaved(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(local);
    setDirty(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <form onSubmit={handleSubmit} aria-label="Bank details">
      <div className="form-group">
        <label htmlFor="settings-bankName">Account Name</label>
        <input id="settings-bankName" type="text" name="bankName" value={local.bankName} onChange={handleChange} />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="settings-bsb">BSB</label>
          <input id="settings-bsb" type="text" name="bsb" value={local.bsb} onChange={handleChange} placeholder="XXX-XXX" />
        </div>
        <div className="form-group">
          <label htmlFor="settings-accountNumber">Account Number</label>
          <input id="settings-accountNumber" type="text" name="accountNumber" value={local.accountNumber} onChange={handleChange} />
        </div>
      </div>
      <div className="settings-form-footer">
        {dirty && <span className="settings-unsaved">Unsaved changes</span>}
        <button type="submit" className="btn btn-primary" disabled={!dirty}>Save Changes</button>
        {saved && <span className="settings-saved">✓ Saved!</span>}
      </div>
    </form>
  );
}

// ─── Invoices Tab ───────────────────────────────────────────────
function InvoicesTab({ settings, onSave }) {
  const [local, setLocal] = useState({
    invoicePrefix: settings.invoicePrefix || '',
    invoiceCounter: settings.invoiceCounter || '',
    defaultInvoiceDueDays: settings.defaultInvoiceDueDays || '',
  });
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocal(prev => ({ ...prev, [name]: value }));
    setDirty(true);
    setSaved(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(local);
    setDirty(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <form onSubmit={handleSubmit} aria-label="Invoice settings">
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="settings-invoicePrefix">Invoice Prefix</label>
          <input id="settings-invoicePrefix" type="text" name="invoicePrefix" value={local.invoicePrefix} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="settings-invoiceCounter">Next Invoice Number</label>
          <input id="settings-invoiceCounter" type="number" name="invoiceCounter" value={local.invoiceCounter} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="settings-defaultDueDays">Default Due Days</label>
          <input id="settings-defaultDueDays" type="number" name="defaultInvoiceDueDays" value={local.defaultInvoiceDueDays} onChange={handleChange} />
        </div>
      </div>
      <div className="settings-form-footer">
        {dirty && <span className="settings-unsaved">Unsaved changes</span>}
        <button type="submit" className="btn btn-primary" disabled={!dirty}>Save Changes</button>
        {saved && <span className="settings-saved">✓ Saved!</span>}
      </div>
    </form>
  );
}

// ─── Appearance Tab ─────────────────────────────────────────────
function AppearanceTab() {
  return (
    <div>
      <ThemeSelector />
    </div>
  );
}

// ─── Security Tab ───────────────────────────────────────────────
function SecurityTab() {
  const { currentUser } = useAuth();
  const isEmailUser = currentUser?.providerData?.some(p => p.providerId === 'password');

  const [passwordData, setPasswordData] = useState({
    currentPassword: '', newPassword: '', confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    setPasswordError('');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }

    try {
      const user = currentUser;
      const credential = EmailAuthProvider.credential(user.email, passwordData.currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, passwordData.newPassword);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordSuccess(true);
      setTimeout(() => setPasswordSuccess(false), 5000);
    } catch (error) {
      if (error.code === 'auth/wrong-password') setPasswordError('Current password is incorrect');
      else if (error.code === 'auth/weak-password') setPasswordError('New password is too weak');
      else setPasswordError('Failed to change password. Please try again.');
    }
  };

  return (
    <div>
      <div className="security-info">
        <p><strong>Email:</strong> {currentUser?.email}</p>
        <p><strong>Sign-in method:</strong> {isEmailUser ? 'Email & Password' : 'Google'}</p>
      </div>

      {isEmailUser ? (
        <form onSubmit={handlePasswordChange} aria-label="Change password">
          <h3>Change Password</h3>
          <div className="form-group">
            <label htmlFor="settings-currentPassword">Current Password</label>
            <input id="settings-currentPassword" type="password" name="currentPassword" value={passwordData.currentPassword} onChange={handlePasswordInputChange} required autoComplete="current-password" />
          </div>
          <div className="form-group">
            <label htmlFor="settings-newPassword">New Password</label>
            <input id="settings-newPassword" type="password" name="newPassword" value={passwordData.newPassword} onChange={handlePasswordInputChange} required autoComplete="new-password" minLength="6" />
            <small>Must be at least 6 characters</small>
          </div>
          <div className="form-group">
            <label htmlFor="settings-confirmPassword">Confirm New Password</label>
            <input id="settings-confirmPassword" type="password" name="confirmPassword" value={passwordData.confirmPassword} onChange={handlePasswordInputChange} required autoComplete="new-password" />
          </div>
          {passwordError && <div className="error-message" style={{ marginBottom: '1rem' }}>{passwordError}</div>}
          {passwordSuccess && <div className="success-message" style={{ marginBottom: '1rem' }}>✓ Password changed successfully!</div>}
          <button type="submit" className="btn btn-primary">Change Password</button>
        </form>
      ) : (
        <div className="security-note">
          <p>Your account is managed through Google. To change your password or security settings, visit your <a href="https://myaccount.google.com/security" target="_blank" rel="noopener noreferrer">Google Account settings</a>.</p>
        </div>
      )}
    </div>
  );
}

// ─── Data Tab ───────────────────────────────────────────────────
function DataTab() {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteText, setDeleteText] = useState('');

  const handleExport = () => {
    const data = exportAllData();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ndis-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = JSON.parse(event.target.result);
          importAllData(data);
          alert('Data imported successfully!');
          window.location.reload();
        } catch (error) {
          alert('Error importing data. Please check the file format.');
        }
      };
      reader.readAsText(file);
    }
  };

  const handleClearAll = () => {
    clearAllData();
    window.location.reload();
  };

  return (
    <div>
      <p style={{ marginBottom: '1.5rem' }}>Download a backup of all your data or restore from a previous backup file.</p>
      <div className="settings-data-actions">
        <button onClick={handleExport} className="btn btn-secondary">📥 Export All Data</button>
        <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
          📤 Import Data
          <input type="file" accept=".json" onChange={handleImport} style={{ display: 'none' }} />
        </label>
      </div>

      <div className="settings-danger-zone">
        <h3>⚠️ Danger Zone</h3>
        <p>Permanently delete all your data including clients, shifts, invoices, and settings. This action cannot be undone.</p>
        {!showDeleteConfirm ? (
          <button className="btn btn-danger" onClick={() => setShowDeleteConfirm(true)}>Clear All Data</button>
        ) : (
          <div className="delete-confirm-box">
            <p><strong>Type DELETE to confirm:</strong></p>
            <input
              type="text"
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              placeholder="Type DELETE"
              autoFocus
            />
            <div className="delete-confirm-actions">
              <button className="btn btn-danger" disabled={deleteText !== 'DELETE'} onClick={handleClearAll}>
                Permanently Delete Everything
              </button>
              <button className="btn btn-secondary" onClick={() => { setShowDeleteConfirm(false); setDeleteText(''); }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Subscription Tab ───────────────────────────────────────────
function SubscriptionTab() {
  const { isPro, isLoading, subscription, isOwner } = useSubscription();
  const [portalLoading, setPortalLoading] = useState(false);
  const navigate = useNavigate();

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const functions = getFunctions(undefined, 'australia-southeast1');
      const createPortalSession = httpsCallable(functions, 'createPortalSession');
      const result = await createPortalSession();
      window.location.href = result.data.url;
    } catch (error) {
      alert('Something went wrong. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  if (isLoading) {
    return <div>Loading subscription info...</div>;
  }

  return (
    <div>
      <div className="security-info" style={{ marginBottom: 'var(--space-xl)' }}>
        <p><strong>Current Plan:</strong> {isPro ? '⭐ Pro' : 'Free'}</p>
        {isOwner && <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>Owner account — lifetime Pro access</p>}
        {subscription?.cancelAtPeriodEnd && (
          <p style={{ color: 'var(--color-warning)', marginTop: 'var(--space-sm)' }}>
            Your subscription will cancel at the end of the current billing period.
          </p>
        )}
      </div>

      {isPro && !isOwner ? (
        <button
          className="btn btn-secondary"
          onClick={handleManageSubscription}
          disabled={portalLoading}
        >
          {portalLoading ? 'Loading...' : '🔧 Manage Subscription'}
        </button>
      ) : !isPro ? (
        <button
          className="btn btn-primary"
          onClick={() => navigate('/pricing')}
        >
          ⭐ Upgrade to Pro
        </button>
      ) : null}
    </div>
  );
}

// ─── Autosend Tab ───────────────────────────────────────────────
function AutosendTab() {
  const schedules = getRecurringSchedules();
  const clients = getClients();
  const agreements = getServiceAgreements();

  // Build list of clients with rosters
  const rostered = schedules.map(sched => {
    const agreement = agreements.find(a => a.id === sched.agreementId);
    const client = agreement ? clients.find(c => c.id === agreement.participant?.id) : null;
    return { schedule: sched, agreement, client };
  }).filter(r => r.client);

  const [autoSettings, setAutoSettings] = useState(() => {
    const initial = {};
    rostered.forEach(({ schedule }) => {
      initial[schedule.id] = schedule.autoInvoice || {
        enabled: false,
        frequency: 'fortnightly',
        startDate: '',
        invoiceDay: 'monday',
        sendMethod: 'review-first',
        planManagerEmail: '',
      };
    });
    return initial;
  });

  const [saved, setSaved] = useState({});

  const updateSetting = (scheduleId, field, value) => {
    setAutoSettings(prev => {
      const updated = { ...prev[scheduleId], [field]: value };
      prev = { ...prev, [scheduleId]: updated };
      // Save to storage
      const sched = rostered.find(r => r.schedule.id === scheduleId)?.schedule;
      if (sched) {
        saveRecurringSchedule({ ...sched, autoInvoice: updated, updatedAt: new Date().toISOString() });
        setSaved(s => ({ ...s, [scheduleId]: true }));
        setTimeout(() => setSaved(s => ({ ...s, [scheduleId]: false })), 2000);
      }
      return prev;
    });
  };

  if (rostered.length === 0) {
    return (
      <div className="settings-placeholder">
        <div className="settings-placeholder-icon"><Zap size={32} /></div>
        <p>Create a roster for a client to unlock auto-invoicing</p>
      </div>
    );
  }

  return (
    <div className="autosend-tab">
      {rostered.map(({ schedule, client }) => {
        const s = autoSettings[schedule.id] || {};
        const clientName = client?.name || 'Unknown Client';
        return (
          <div key={schedule.id} className="autosend-client-card">
            <div className="autosend-client-header">
              <h3>{clientName}</h3>
              <div className="autosend-enable-row">
                <button
                  className={`toggle-switch-settings ${s.enabled ? 'on' : ''}`}
                  onClick={() => updateSetting(schedule.id, 'enabled', !s.enabled)}
                  aria-label={`Toggle auto-invoicing for ${clientName}`}
                >
                  <span className="toggle-knob" />
                </button>
                {s.enabled && <span className="autosend-active-badge">Active</span>}
                {saved[schedule.id] && <span className="settings-saved">✓ Saved</span>}
              </div>
            </div>

            {s.enabled && (
              <div className="autosend-fields">
                <div className="form-row">
                  <div className="form-group">
                    <label>Invoice Frequency</label>
                    <select value={s.frequency} onChange={e => updateSetting(schedule.id, 'frequency', e.target.value)}>
                      <option value="weekly">Weekly</option>
                      <option value="fortnightly">Fortnightly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Invoice Day</label>
                    <select value={s.invoiceDay} onChange={e => updateSetting(schedule.id, 'invoiceDay', e.target.value)}>
                      <option value="monday">Monday</option>
                      <option value="tuesday">Tuesday</option>
                      <option value="wednesday">Wednesday</option>
                      <option value="thursday">Thursday</option>
                      <option value="friday">Friday</option>
                      <option value="saturday">Saturday</option>
                      <option value="sunday">Sunday</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={s.startDate}
                      onChange={e => updateSetting(schedule.id, 'startDate', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Send Method</label>
                    <select value={s.sendMethod} onChange={e => updateSetting(schedule.id, 'sendMethod', e.target.value)}>
                      <option value="review-first">SMS me to review first</option>
                      <option value="auto-send">Send automatically</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Plan Manager Email</label>
                  <input
                    type="email"
                    value={s.planManagerEmail || client?.planManagerEmail || ''}
                    onChange={e => updateSetting(schedule.id, 'planManagerEmail', e.target.value)}
                    placeholder="pm@example.com"
                  />
                </div>

                <p className="autosend-hint">
                  {s.sendMethod === 'review-first'
                    ? `We'll generate the invoice and SMS you to approve before sending.`
                    : `Invoices will be automatically generated and emailed to the plan manager.`}
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Coming Soon Placeholder ────────────────────────────────────
function ComingSoonTab({ icon, label }) {
  return (
    <div className="settings-placeholder">
      <div className="settings-placeholder-icon">{icon}</div>
      <p>{label} is coming soon</p>
    </div>
  );
}

// ─── Main Settings Component ────────────────────────────────────
function Settings() {
  const [activeTab, setActiveTab] = useState('profile');
  const [settings, setSettings] = useState(getSettings());
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const TABS = getTabs();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSave = (partialSettings) => {
    const updated = { ...settings, ...partialSettings };
    setSettings(updated);
    saveSettings(updated);
  };

  const activeTabDef = TABS.find(t => t.id === activeTab);
  const lastActiveIndex = TABS.reduce((acc, tab, i) => tab.disabled ? acc : i, 0);

  // Keyboard navigation for sidebar
  const handleTabKeyDown = (e, index) => {
    const enabledTabs = TABS.filter(t => !t.disabled);
    const currentEnabledIndex = enabledTabs.findIndex(t => t.id === TABS[index].id);
    if (currentEnabledIndex === -1) return;
    let newIndex = currentEnabledIndex;

    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      newIndex = (currentEnabledIndex + 1) % enabledTabs.length;
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      newIndex = (currentEnabledIndex - 1 + enabledTabs.length) % enabledTabs.length;
    } else if (e.key === 'Home') {
      e.preventDefault();
      newIndex = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      newIndex = enabledTabs.length - 1;
    } else {
      return;
    }

    const newTab = enabledTabs[newIndex];
    setActiveTab(newTab.id);
    document.getElementById(`tab-${newTab.id}`)?.focus();
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'profile': return <ProfileTab settings={settings} onSave={handleSave} />;
      case 'bank': return <BankTab settings={settings} onSave={handleSave} />;
      case 'invoices': return <InvoicesTab settings={settings} onSave={handleSave} />;
      case 'appearance': return <AppearanceTab />;
      case 'security': return <SecurityTab />;
      case 'data': return <DataTab />;
      case 'subscription': return <SubscriptionTab />;
      case 'autosend': return null;
      case 'autokm': return <ComingSoonTab icon={<MapPin size={32} />} label="Auto-Calculate Shift KMs" />;
      default: return null;
    }
  };

  return (
    <div className="settings-page">
      <h1>Settings</h1>
      <div className="settings-layout">
        {isMobile ? (
          <div className="settings-mobile-nav">
            <div className="settings-mobile-nav-bar">
              <span className="settings-mobile-active">
                <span className="settings-tab-icon">{activeTabDef?.icon}</span> {activeTabDef?.label}
              </span>
              <button className="settings-hamburger" onClick={() => setMenuOpen(!menuOpen)} aria-label="Settings menu">
                <span className={`hamburger-icon ${menuOpen ? 'open' : ''}`}>
                  <span></span><span></span><span></span>
                </span>
              </button>
            </div>
            {menuOpen && (
              <div className="settings-mobile-menu">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    className={`settings-mobile-menu-item ${activeTab === tab.id ? 'active' : ''} ${tab.disabled ? 'disabled' : ''}`}
                    onClick={() => { if (!tab.disabled) { setActiveTab(tab.id); setMenuOpen(false); } }}
                    disabled={tab.disabled}
                  >
                    <span className="settings-tab-icon">{tab.icon}</span>
                    <span>{tab.label}</span>
                    {tab.comingSoon && <span className="settings-tab-badge">Soon</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <nav className="settings-sidebar" role="tablist" aria-label="Settings sections">
            {TABS.map((tab, index) => (
              <React.Fragment key={tab.id}>
                {index === lastActiveIndex + 1 && <div className="settings-sidebar-divider" />}
                <button
                  role="tab"
                  id={`tab-${tab.id}`}
                  aria-selected={activeTab === tab.id}
                  aria-controls={`panel-${tab.id}`}
                  className={`settings-tab ${activeTab === tab.id ? 'active' : ''} ${tab.disabled ? 'disabled' : ''}`}
                  onClick={() => !tab.disabled && setActiveTab(tab.id)}
                  onKeyDown={(e) => handleTabKeyDown(e, index)}
                  tabIndex={activeTab === tab.id ? 0 : -1}
                  disabled={tab.disabled}
                >
                  <span className="settings-tab-icon">{tab.icon}</span>
                  <span className="settings-tab-label">{tab.label}</span>
                  {tab.comingSoon && <span className="settings-tab-badge">Soon</span>}
                </button>
              </React.Fragment>
            ))}
          </nav>
        )}
        <main
          className="settings-content"
          role="tabpanel"
          id={`panel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
          key={activeTab}
        >
          <div className="settings-content-header">
            <h2>{activeTabDef?.label}</h2>
            <p className="settings-content-description">{activeTabDef?.description}</p>
          </div>
          {renderContent()}
        </main>
      </div>
    </div>
  );
}

export default Settings;
